import { useMemo } from "react";

import { HttpError, useDataProvider, useGetIdentity } from "@refinedev/core";

import { Organization, PractitionerRole } from "@/fhir/types";
import { FIVE_MINUTE_STALE_TIME } from "@/providers/data/fhir-data-provider";
import { IUser } from "@/types";
import { FhirResourceType, formatters } from "@/utils/fhir-utils";

import * as Sentry from "@sentry/react";
import { fhirR4 } from "@smile-cdr/fhirts";
import { useQuery } from "@tanstack/react-query";

export interface UsePractitionerOrganizationResult {
  organizationReference: string | undefined;
  isLoading: boolean;
  error: HttpError | null;
}

/**
 * Fetches the practitioner's active PractitionerRoles with _include=PractitionerRole:organization,
 * then resolves to the parent organization (the one without a partOf reference).
 * All consumers expect this to return the top-level org for fetching child clinics, org practitioners, etc.
 */
export const usePractitionerOrganization = (): UsePractitionerOrganizationResult => {
  const { data: user } = useGetIdentity<IUser>();
  const fhirPractitionerId = user?.fhirPractitionerId;
  const dataProvider = useDataProvider();

  const queryKey = useMemo(
    () => [FhirResourceType.PractitionerRole, "practitioner-organization", fhirPractitionerId ?? ""],
    [fhirPractitionerId]
  );

  const query = useQuery<{ roles: PractitionerRole[]; organizations: Organization[] }, HttpError>({
    queryKey,
    enabled: !!fhirPractitionerId,
    staleTime: FIVE_MINUTE_STALE_TIME,
    queryFn: async () => {
      const { data } = await dataProvider().getList<PractitionerRole>({
        resource: FhirResourceType.PractitionerRole,
        filters: [
          { field: "practitioner", operator: "eq", value: fhirPractitionerId! },
          { field: "active", operator: "eq", value: true },
        ],
        meta: {
          fetchAllPages: true,
          pageSize: 100,
          customQueryParameters: ["_include=PractitionerRole:organization", "_include:iterate=Organization:partof"],
        },
      });

      const allResources = (data as fhirR4.Resource[]) ?? [];
      const roles: PractitionerRole[] = [];
      const organizations: Organization[] = [];

      for (const resource of allResources) {
        if (!resource) {
          continue;
        }
        if (resource.resourceType === FhirResourceType.PractitionerRole) {
          roles.push(resource as PractitionerRole);
        } else if (resource.resourceType === FhirResourceType.Organization) {
          organizations.push(resource as Organization);
        }
      }

      return { roles, organizations };
    },
  });

  const organizationReference = useMemo(() => {
    if (!query.data) {
      return undefined;
    }

    const { roles, organizations } = query.data;

    // Deduplicate organizations by id — FHIR _include queries can return
    // the same resource multiple times when a practitioner has multiple roles
    // pointing to the same org.
    const uniqueOrgs = organizations.filter((org, idx, arr) => arr.findIndex(o => o.id === org.id) === idx);

    // Find parent orgs — those without a partOf reference
    const parentOrgs = uniqueOrgs.filter(org => !org.partOf?.reference);
    if (parentOrgs.length > 1) {
      Sentry.captureMessage("Multiple parent organizations found for practitioner", {
        level: "warning",
        extra: { orgIds: parentOrgs.map(o => o.id) },
      });
    }

    // Use the first parent org for now — future: practitioner selects their org on login
    const parentOrg = parentOrgs[0];
    if (parentOrg?.id) {
      return formatters.formatOrganization(parentOrg.id);
    }

    // Fallback: no parent org found — this shouldn't happen in normal data
    const fallbackRef = roles[0]?.organization?.reference;
    if (fallbackRef) {
      Sentry.captureMessage("No parent organization found for practitioner, using fallback", {
        level: "warning",
        extra: { fallbackRef, orgIds: uniqueOrgs.map(o => o.id) },
      });
    }
    return fallbackRef;
  }, [query.data]);

  return {
    organizationReference,
    isLoading: query.isLoading,
    error: query.error ?? null,
  };
};
